import type { Product as ShowcaseProduct, TeaProduct } from '@/types/common'
import { SHOP_PRODUCTS, TEA_PRODUCTS, TEA_PRODUCT_NAMES } from '@/constants/products'
import { buildCloudflareImageUrl } from '@/utils'
import { apiClient } from './client'

interface ApiResponse<T> {
  success: boolean
  data?: T
  message?: string
}

interface PagedResult<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
}

interface ProductImage {
  id: string
  url: string
  alt: string
  isPrimary: boolean
  sortOrder: number
}

interface ApiProduct {
  id: string
  key: string
  name: string
  description: string
  price: number
  priceTwd?: number
  images: ProductImage[]
  category: string
  format: string
  stock?: number
  sku: string
  createdAt?: string
  updatedAt?: string
}

interface ApiProductListItem {
  id: string
  key: string
  name?: string
  price: number
  priceTwd?: number
  category?: string
  format?: string
  stock?: number
  primaryImageId?: string
  primaryImageUrl?: string
}

export interface ProductListParams {
  page?: number
  limit?: number
  category?: string
  format?: string
  search?: string
}

export interface ProductListResponse {
  data: TeaProduct[]
  total: number
  page: number
  limit: number
}

const isTeaProductKey = (value: string): value is TeaProduct['key'] =>
  value in TEA_PRODUCT_NAMES

const normalizeProductKey = (value: string): TeaProduct['key'] =>
  isTeaProductKey(value) ? value : 'i'

const getFallbackProduct = (key: TeaProduct['key'], format?: string) =>
  SHOP_PRODUCTS.find(product => product.key === key && product.format === format)
  ?? SHOP_PRODUCTS.find(product => product.key === key)
  ?? TEA_PRODUCTS.find(product => product.key === key)

const getFallbackProductByRouteId = (routeId: string): TeaProduct | undefined => {
  const exactMatch = SHOP_PRODUCTS.find(product => getProductRouteId(product) === routeId)
    ?? TEA_PRODUCTS.find(product => getProductRouteId(product) === routeId)

  if (exactMatch) {
    return exactMatch
  }

  const [key, format] = routeId.split('-', 2)
  if (!isTeaProductKey(key)) {
    return undefined
  }

  return getFallbackProduct(key, format)
}

export const getProductRouteId = (product: Pick<TeaProduct, 'id' | 'key' | 'format'>): string =>
  product.id || (product.format ? `${product.key}-${product.format}` : product.key)

const resolveProductImage = (
  imageId: string | undefined,
  key: TeaProduct['key'],
  format?: string,
  apiImageUrl?: string
) => {
  const cloudflareUrl = buildCloudflareImageUrl(imageId, undefined, apiImageUrl)
  if (cloudflareUrl) {
    return cloudflareUrl
  }

  if (apiImageUrl) {
    return apiImageUrl
  }

  return getFallbackProduct(key, format)?.image || ''
}

const mapListItemToTeaProduct = (item: ApiProductListItem): TeaProduct => {
  const key = normalizeProductKey(item.key)
  const fallbackProduct = getFallbackProduct(key, item.format)

  return {
    id: item.id,
    key,
    imageId: item.primaryImageId,
    image: resolveProductImage(item.primaryImageId, key, item.format, item.primaryImageUrl),
    images: fallbackProduct?.images,
    price: item.price,
    priceTwd: item.priceTwd,
    name: item.name,
    category: item.category,
    format: item.format,
    stock: item.stock,
    hasVariants: fallbackProduct?.hasVariants ?? item.category === 'gift-sets',
  }
}

const mapProductToTeaProduct = (product: ApiProduct): TeaProduct => {
  const key = normalizeProductKey(product.key)
  const primaryImage = [...product.images]
    .sort((left, right) => {
      if (left.isPrimary === right.isPrimary) {
        return left.sortOrder - right.sortOrder
      }

      return left.isPrimary ? -1 : 1
    })[0]

  const fallbackProduct = getFallbackProduct(key, product.format)
  const sortedImages = [...product.images]
    .sort((left, right) => {
      if (left.isPrimary === right.isPrimary) {
        return left.sortOrder - right.sortOrder
      }

      return left.isPrimary ? -1 : 1
    })
  const galleryImages = sortedImages
    .map(image => resolveProductImage(image.id, key, product.format, image.url))
    .filter(Boolean)

  return {
    id: product.id,
    key,
    imageId: primaryImage?.id,
    image: resolveProductImage(primaryImage?.id, key, product.format, primaryImage?.url),
    images: galleryImages.length ? galleryImages : fallbackProduct?.images,
    price: product.price,
    priceTwd: product.priceTwd,
    name: product.name,
    description: product.description,
    category: product.category,
    format: product.format,
    stock: product.stock,
    sku: product.sku,
    hasVariants: fallbackProduct?.hasVariants ?? product.category === 'gift-sets',
  }
}

const mapParams = (params?: ProductListParams) => ({
  page: params?.page,
  pageSize: params?.limit,
  category: params?.category,
  format: params?.format,
  searchKeyword: params?.search,
})

export const productsApi = {
  async getList(params?: ProductListParams): Promise<ProductListResponse> {
    const response = await apiClient.get<PagedResult<ApiProductListItem>>('/api/products', mapParams(params))
    return {
      data: response.items.map(mapListItemToTeaProduct),
      total: response.totalCount,
      page: response.page,
      limit: response.pageSize,
    }
  },

  async getCatalogProducts(params?: ProductListParams): Promise<TeaProduct[]> {
    const response = await this.getList(params)
    return response.data
  },

  async getFeaturedProducts(limit = 4): Promise<TeaProduct[]> {
    const response = await apiClient.get<PagedResult<ApiProductListItem>>('/api/products/featured', {
      page: 1,
      pageSize: limit,
    })

    return response.items.map(mapListItemToTeaProduct).slice(0, limit)
  },

  async getShowcaseProducts(limit = 3): Promise<ShowcaseProduct[]> {
    const response = await apiClient.get<PagedResult<ApiProductListItem>>('/api/products/showcase', {
      page: 1,
      pageSize: limit,
    })
    const products = response.items.map(mapListItemToTeaProduct)

    return products.slice(0, limit).map((product, index) => ({
      id: product.id || `${product.key}-${index}`,
      name: product.name || TEA_PRODUCT_NAMES[product.key].en,
      image: product.image,
      price: product.price.toFixed(1),
      description: product.description,
      category: product.category,
    }))
  },

  async getById(id: string): Promise<TeaProduct | null> {
    try {
      const response = await apiClient.get<ApiResponse<ApiProduct>>(`/api/products/${id}`)

      if (!response.success || !response.data) {
        return null
      }

      return mapProductToTeaProduct(response.data)
    } catch {
      return null
    }
  },

  async getByRouteId(routeId: string): Promise<TeaProduct | null> {
    const product = await this.getById(routeId)
    if (product) {
      return product
    }

    const fallbackProduct = getFallbackProductByRouteId(routeId)
    if (!fallbackProduct) {
      return null
    }

    return {
      ...fallbackProduct,
      id: getProductRouteId(fallbackProduct),
      name: fallbackProduct.name || TEA_PRODUCT_NAMES[fallbackProduct.key].en,
    }
  },

  async search(query: string): Promise<TeaProduct[]> {
    return this.getCatalogProducts({ search: query })
  },

  async getByCategory(category: string): Promise<TeaProduct[]> {
    return this.getCatalogProducts({ category })
  },

  async create(data: unknown): Promise<ApiProduct> {
    return apiClient.post<ApiProduct>('/api/admin/products', data)
  },

  async update(id: string, data: unknown): Promise<ApiProduct> {
    return apiClient.put<ApiProduct>(`/api/admin/products/${id}`, data)
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/products/${id}`)
  },

  async updateStock(id: string, stock: number): Promise<ApiProduct> {
    return apiClient.patch<ApiProduct>(`/api/admin/products/${id}/stock`, { stock })
  },
}
